import { Box, Stack, useTheme } from '@mui/material';
import { Children, Fragment, isValidElement, ReactNode, useId } from 'react';

import { Button, ButtonProps } from '@/components/form/baseInputs/Button';
import {
  ButtonWithPopover,
  ButtonWithPopoverProps,
  ButtonWithPopoverSize,
} from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { ButtonSize } from '@/components/form/baseInputs/Button/types';
import { COLORS } from '@/styles/tokens/colors';

import {
  SlideToolbarPopoverGroupProvider,
  useSlideToolbarPopoverGroup,
} from './SlideToolbarPopoverGroup';

/**
 * Button size shared by the toolbars rendered inside `SlideToolbar` (e.g. the
 * entities-list row toolbar), so child toolbars stay the same compact height
 * as the rich-text / title editor toolbars.
 */
export const SLIDE_TOOLBAR_BUTTON_SIZE = 'xs' as const;

/**
 * Group-divider hints read by `SlideToolbar` to place a vertical divider
 * between groups of buttons. They are consumed by the parent toolbar, not the
 * underlying button, so the same divider chrome is owned by `SlideToolbar`
 * rather than re-declared by each consumer.
 */
export interface SlideToolbarGroupProps {
  /**
   * Render a single group divider before this item. Ignored on the first item
   * (no leading divider).
   */
  startsGroup?: boolean;
  /**
   * Render a single group divider after this item. Ignored on the last item
   * (no trailing divider).
   */
  endsGroup?: boolean;
}

export type SlideToolbarButtonProps = Omit<ButtonProps, 'size'> & {
  size?: ButtonSize;
} & SlideToolbarGroupProps;

/**
 * `Button` pre-sized for slide toolbars. Use this inside a `SlideToolbar`
 * instead of a bare `Button` so the toolbar — not each consumer — controls the
 * button size. An explicit `size` still wins if a toolbar needs to override it.
 */
export function SlideToolbarButton({
  size = SLIDE_TOOLBAR_BUTTON_SIZE,
  // `startsGroup` / `endsGroup` are read by `SlideToolbar` off this element's
  // props to place group dividers; they aren't `Button` props, so drop them
  // rather than forwarding them onto the DOM.
  startsGroup: _startsGroup,
  endsGroup: _endsGroup,
  onClick,
  ...props
}: SlideToolbarButtonProps) {
  const popoverGroup = useSlideToolbarPopoverGroup();

  return (
    <Button
      size={size}
      onClick={(event) => {
        // Clicking any toolbar button collapses a sibling's open menu, so an
        // action never runs with a stale menu left open over the slide.
        popoverGroup?.closeAllPopovers();
        onClick?.(event);
      }}
      {...props}
    />
  );
}

export type SlideToolbarButtonWithPopoverProps = Omit<
  ButtonWithPopoverProps,
  'size'
> & {
  size?: ButtonWithPopoverSize;
} & SlideToolbarGroupProps;

/**
 * `ButtonWithPopover` pre-sized for slide toolbars. See `SlideToolbarButton`.
 *
 * Inside a `SlideToolbar` the popover is driven by the toolbar's popover group,
 * so only one of the toolbar's menus is ever open (see
 * `SlideToolbarPopoverGroupProvider`). A caller-supplied `isOpen` still wins,
 * and outside a `SlideToolbar` the button manages its own open state as usual.
 */
export function SlideToolbarButtonWithPopover({
  size = SLIDE_TOOLBAR_BUTTON_SIZE,
  // See `SlideToolbarButton`: consumed by `SlideToolbar`, not forwarded.
  startsGroup: _startsGroup,
  endsGroup: _endsGroup,
  isOpen,
  onClick,
  onClose,
  ...props
}: SlideToolbarButtonWithPopoverProps) {
  const popoverGroup = useSlideToolbarPopoverGroup();
  const popoverId = useId();

  const isGroupControlled = !!popoverGroup && isOpen === undefined;
  const isGroupOpen = popoverGroup?.openPopoverId === popoverId;

  return (
    <ButtonWithPopover
      size={size}
      isOpen={isGroupControlled ? isGroupOpen : isOpen}
      onClick={(event) => {
        if (isGroupControlled) {
          if (isGroupOpen) {
            popoverGroup.closePopover(popoverId);
          } else {
            popoverGroup.openPopover(popoverId);
          }
        }
        onClick?.(event);
      }}
      onClose={() => {
        popoverGroup?.closePopover(popoverId);
        onClose?.();
      }}
      {...props}
    />
  );
}

interface SlideToolbarProps {
  children: ReactNode;
}

/**
 * The vertical divider `SlideToolbar` inserts between button groups. 1px × 16px
 * in GRAY[200], matching the diagram toolbar's `VerticalDivider`. Horizontal
 * spacing comes from the toolbar row's 4px gap, not a margin.
 */
function SlideToolbarDivider() {
  return <Box sx={{ width: '1px', height: 16, bgcolor: COLORS.GRAY[200] }} />;
}

/**
 * Flatten Fragment children so `startsGroup` / `endsGroup` divider hints stay
 * visible when a consumer groups buttons in a `<>...</>` (or a render helper
 * returns one). `Children.toArray` keeps a Fragment as a single opaque item,
 * which would hide the buttons — and their group props — from the divider logic
 * below.
 */
function flattenToolbarChildren(children: ReactNode): ReactNode[] {
  return Children.toArray(children).flatMap((child) =>
    isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment
      ? flattenToolbarChildren(child.props.children)
      : [child]
  );
}

function itemStartsGroup(item: ReactNode): boolean {
  return (
    isValidElement<SlideToolbarGroupProps>(item) && !!item.props.startsGroup
  );
}

function itemEndsGroup(item: ReactNode): boolean {
  return isValidElement<SlideToolbarGroupProps>(item) && !!item.props.endsGroup;
}

/**
 * Toolbar container for slide-related toolbars. Provides consistent visual
 * chrome: white background, border, rounded corners, shadow, and horizontal
 * layout. Use `SlideToolbarButton` / `SlideToolbarButtonWithPopover` for its
 * contents so the buttons pick up the toolbar's size automatically — and so
 * the toolbar keeps itself to one open popover at a time (see
 * `SlideToolbarPopoverGroupProvider`).
 *
 * A child with `startsGroup` / `endsGroup` gets a single vertical divider at
 * that boundary. The divider is always placed *before* an item — when the item
 * starts a group or the previous item ended one — so a boundary marked from
 * either side (or both) still renders exactly one divider, never two adjacent
 * ones, and never a leading or trailing divider. Fragment children are
 * flattened first, so consumers may group buttons in a `<>...</>` without
 * hiding their group props.
 *
 * For toolbars that need dropdown coordination (e.g. text editor toolbars),
 * use `SlideEditorToolbar` which composes this component with a dropdown
 * context provider.
 */
export function SlideToolbar({ children }: SlideToolbarProps) {
  const theme = useTheme();
  const items = flattenToolbarChildren(children);

  return (
    <SlideToolbarPopoverGroupProvider>
      <Stack
        sx={{
          bgcolor: 'white',
          border: `1px solid ${COLORS.GRAY[200]}`,
          borderRadius: '6px',
          p: '3px',
          boxShadow: theme.palette.shadows.lg,
          width: 'fit-content',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {items.map((item, index) => {
            const needsDivider =
              index > 0 &&
              (itemStartsGroup(item) || itemEndsGroup(items[index - 1]));
            return (
              // Static, non-reordered list, so the index is a stable key.
              <Fragment key={index}>
                {needsDivider && <SlideToolbarDivider />}
                {item}
              </Fragment>
            );
          })}
        </Stack>
      </Stack>
    </SlideToolbarPopoverGroupProvider>
  );
}
